import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from "react";
import { toast } from "react-toastify";
import {
  getOfflinePosQueueCount,
  OFFLINE_POS_QUEUE_CHANGE,
} from "../lib/offlinePosQueue";
import { isBrowserOffline } from "../lib/networkStatus";
import {
  OfflinePosSyncResult,
  syncOfflinePosQueue,
} from "../services/offlinePosSync";

interface OfflineContextValue {
  isOffline: boolean;
  showReconnected: boolean;
  pendingOfflineSales: number;
  isSyncingOfflineSales: boolean;
  syncOfflineSales: () => Promise<OfflinePosSyncResult>;
}

const OfflineContext = createContext<OfflineContextValue | null>(null);

const RECONNECT_SYNC_DELAY_MS = 400;

export function OfflineProvider({ children }: { children: ReactNode }) {
  const [isOffline, setIsOffline] = useState(
    () => typeof navigator !== "undefined" && !navigator.onLine,
  );
  const [showReconnected, setShowReconnected] = useState(false);
  const [pendingOfflineSales, setPendingOfflineSales] = useState(
    getOfflinePosQueueCount,
  );
  const [isSyncingOfflineSales, setIsSyncingOfflineSales] = useState(false);
  const syncInFlightRef = useRef(false);
  const reconnectSyncTimerRef = useRef<number | null>(null);

  const refreshPendingCount = useCallback(() => {
    setPendingOfflineSales(getOfflinePosQueueCount());
  }, []);

  const runSync = useCallback(async (): Promise<OfflinePosSyncResult> => {
    if (syncInFlightRef.current || isBrowserOffline()) {
      return {
        synced: 0,
        failed: 0,
        remaining: getOfflinePosQueueCount(),
      };
    }

    if (getOfflinePosQueueCount() === 0) {
      return { synced: 0, failed: 0, remaining: 0 };
    }

    syncInFlightRef.current = true;
    setIsSyncingOfflineSales(true);
    try {
      const result = await syncOfflinePosQueue();
      refreshPendingCount();

      if (result.synced > 0) {
        toast.success(
          `${result.synced} offline sale${result.synced === 1 ? "" : "s"} synced.`,
        );
      } else if (result.failed > 0 && result.remaining > 0) {
        toast.error(
          `${result.remaining} offline sale${result.remaining === 1 ? "" : "s"} still waiting to sync.`,
        );
      }

      return result;
    } finally {
      syncInFlightRef.current = false;
      setIsSyncingOfflineSales(false);
    }
  }, [refreshPendingCount]);

  const scheduleReconnectSync = useCallback(() => {
    if (reconnectSyncTimerRef.current != null) {
      window.clearTimeout(reconnectSyncTimerRef.current);
    }

    reconnectSyncTimerRef.current = window.setTimeout(() => {
      reconnectSyncTimerRef.current = null;
      if (!isBrowserOffline() && getOfflinePosQueueCount() > 0) {
        void runSync();
      }
    }, RECONNECT_SYNC_DELAY_MS);
  }, [runSync]);

  const syncOfflineSales = runSync;

  useEffect(() => {
    const handleOffline = () => {
      if (reconnectSyncTimerRef.current != null) {
        window.clearTimeout(reconnectSyncTimerRef.current);
        reconnectSyncTimerRef.current = null;
      }
      setIsOffline(true);
      setShowReconnected(false);
    };

    const handleOnline = () => {
      setIsOffline(false);
      setShowReconnected(true);
      scheduleReconnectSync();
    };

    const handleVisibility = () => {
      if (document.visibilityState !== "visible") return;
      if (isBrowserOffline()) return;
      if (getOfflinePosQueueCount() === 0) return;
      scheduleReconnectSync();
    };

    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);
    window.addEventListener("focus", scheduleReconnectSync);
    window.addEventListener(OFFLINE_POS_QUEUE_CHANGE, refreshPendingCount);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("focus", scheduleReconnectSync);
      window.removeEventListener(OFFLINE_POS_QUEUE_CHANGE, refreshPendingCount);
      document.removeEventListener("visibilitychange", handleVisibility);
      if (reconnectSyncTimerRef.current != null) {
        window.clearTimeout(reconnectSyncTimerRef.current);
      }
    };
  }, [refreshPendingCount, scheduleReconnectSync]);

  useEffect(() => {
    if (!showReconnected) return;
    const timer = window.setTimeout(() => setShowReconnected(false), 3000);
    return () => window.clearTimeout(timer);
  }, [showReconnected]);

  useEffect(() => {
    const visible =
      isOffline || showReconnected || pendingOfflineSales > 0;
    document.documentElement.style.setProperty(
      "--offline-banner-height",
      visible ? "40px" : "0px",
    );
    return () => {
      document.documentElement.style.removeProperty("--offline-banner-height");
    };
  }, [isOffline, showReconnected, pendingOfflineSales]);

  const value = useMemo(
    () => ({
      isOffline,
      showReconnected,
      pendingOfflineSales,
      isSyncingOfflineSales,
      syncOfflineSales,
    }),
    [
      isOffline,
      showReconnected,
      pendingOfflineSales,
      isSyncingOfflineSales,
      syncOfflineSales,
    ],
  );

  return (
    <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>
  );
}

export function useOffline() {
  const ctx = useContext(OfflineContext);
  if (!ctx) {
    throw new Error("useOffline must be used within OfflineProvider");
  }
  return ctx;
}
